/** Files staged in a draft (the tour plan §3 "Files"; ImageWidget's `local` staging): the
 *  draft holds only a token entry `{staged, name, size}` — JSON, so it mirrors to
 *  sessionStorage like any value — and the `File` itself lives here, outside the draft,
 *  keyed by that token. A reload keeps the entry but loses the file: the tour's
 *  `localPlan` then asks the operator to re-attach it.
 *
 *  Values of a file path:
 *  - stored: `{ id }` (the current image), or null (none);
 *  - draft: `{ staged, name, size }` (replace with a staged file), or null (clear). */

export interface StagedFileRef {
  /** The token the `File` is kept under. */
  staged: string;
  name: string;
  size: number;
}

export interface StoredFileRef {
  id: number;
}

export function isStagedFile(v: unknown): v is StagedFileRef {
  return typeof v === 'object' && v !== null && typeof (v as StagedFileRef).staged === 'string';
}

export function isStoredFile(v: unknown): v is StoredFileRef {
  return typeof v === 'object' && v !== null && typeof (v as StoredFileRef).id === 'number' && !isStagedFile(v);
}

let seq = 0;

/** token → File, for one page. */
export class StagedFiles {
  private files = new Map<string, File>();

  /** Keep `file`; the returned ref is what the draft holds. */
  stage(file: File): StagedFileRef {
    seq += 1;
    const token = `file:${Date.now().toString(36)}:${seq}`;
    this.files.set(token, file);
    return { staged: token, name: file.name, size: file.size };
  }

  get(ref: StagedFileRef | string): File | undefined {
    return this.files.get(typeof ref === 'string' ? ref : ref.staged);
  }

  has(ref: StagedFileRef | string): boolean {
    return this.files.has(typeof ref === 'string' ? ref : ref.staged);
  }

  /** After a save: the files went with it. */
  clear(): void {
    this.files.clear();
  }
}

export { formatBytes } from '@/utils/formatBytes';
